import { useEffect, useState, useMemo } from "react";
import { api } from "../lib/api";
import { formatDate, formatIDR } from "../lib/format";
import PageHeader from "../components/PageHeader";
import StatusBadge from "../components/StatusBadge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import {
  Search, Star, MessageCircle, Phone, Users, Loader2, Sparkles, Crown, UserPlus, Globe,
} from "lucide-react";
import { toast } from "sonner";
import { COUNTRIES, flagFor } from "../lib/countries";

const TIER_META = {
  loyal: { label: "Tamu Langganan", icon: Crown, cls: "bg-[#D16B4D] text-white border-[#D16B4D]" },
  returning: { label: "Tamu Kembali", icon: Sparkles, cls: "bg-cyan-100 text-cyan-800 border-cyan-300" },
  new: { label: "Tamu Baru", icon: UserPlus, cls: "bg-[#FDFBF7] text-[#586D61] border-[#EBE6DE]" },
};

function TierBadge({ tier }) {
  const meta = TIER_META[tier] || TIER_META.new;
  const Icon = meta.icon;
  return (
    <span
      data-testid={`tier-badge-${tier}`}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${meta.cls}`}
    >
      <Icon className="h-3 w-3" />
      {meta.label}
    </span>
  );
}

const buildHelloMsg = (g) => {
  const lines = [
    `Halo ${g.guest_name}! 🌴`,
    "",
    `Salam hangat dari Meos Ambower Homestay, Raja Ampat.`,
    "",
    `Terima kasih sudah menjadi bagian dari keluarga kami${g.total_bookings >= 2 ? ` dan kembali sebanyak ${g.total_bookings} kali` : ""}. Apa kabar?`,
    "",
    `Jika ada rencana liburan ke Raja Ampat lagi, jangan ragu hubungi kami untuk penawaran spesial. 🏝️`,
  ];
  return lines.join("\n");
};

const waUrl = (g) => {
  const phone = (g.phone_normalized || "").replace(/\D/g, "");
  const msg = encodeURIComponent(buildHelloMsg(g));
  return phone ? `https://wa.me/${phone}?text=${msg}` : `https://wa.me/?text=${msg}`;
};

export default function Guests() {
  const [guests, setGuests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [fCountry, setFCountry] = useState("All");
  const [selected, setSelected] = useState(null);
  const [detailBookings, setDetailBookings] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/guests");
      setGuests(res.data);
    } catch (e) {
      toast.error("Gagal memuat data tamu");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Available countries actually present in DB (for filter dropdown)
  const availableCountries = useMemo(() => {
    const set = new Set();
    guests.forEach((g) => g.country && set.add(g.country));
    return Array.from(set).sort();
  }, [guests]);

  const filtered = useMemo(() => {
    let list = !search.trim()
      ? [...guests]
      : guests.filter(
          (g) =>
            (g.guest_name || "").toLowerCase().includes(search.toLowerCase()) ||
            (g.phone_number || "").toLowerCase().includes(search.toLowerCase()),
        );
    if (fCountry !== "All") {
      list = list.filter((g) => (g.country || "") === fCountry);
    }
    // Sort by proximity to today: upcoming/current at top (closest first), then past by recency.
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayMs = today.getTime();
    list.sort((a, b) => {
      const da = a.last_check_in ? new Date(a.last_check_in).getTime() : 0;
      const db = b.last_check_in ? new Date(b.last_check_in).getTime() : 0;
      const aFuture = da >= todayMs;
      const bFuture = db >= todayMs;
      if (aFuture && !bFuture) return -1;
      if (!aFuture && bFuture) return 1;
      if (aFuture && bFuture) return da - db; // upcoming: closest first
      return db - da;                          // past: most recent first
    });
    return list;
  }, [guests, search, fCountry]);

  const stats = useMemo(() => {
    const total = guests.length;
    const loyal = guests.filter((g) => g.tier === "loyal").length;
    const returning = guests.filter((g) => g.tier === "returning").length;
    const totalRevenue = guests.reduce((s, g) => s + (g.total_revenue || 0), 0);
    return { total, loyal, returning, totalRevenue };
  }, [guests]);

  const openDetail = async (g) => {
    setSelected(g);
    setDetailBookings([]);
    setDetailLoading(true);
    try {
      const params = new URLSearchParams();
      if (g.phone_normalized) params.set("phone", g.phone_normalized);
      else params.set("name", g.guest_name);
      const res = await api.get(`/guests/bookings?${params.toString()}`);
      setDetailBookings(res.data);
    } catch (e) {
      toast.error("Gagal memuat riwayat tamu");
    } finally {
      setDetailLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Database Tamu"
        subtitle="Riwayat semua tamu — total kunjungan, pendapatan, dan loyalitas. Manfaatkan untuk follow-up & program tamu langganan."
      />

      {/* Stats cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard icon={Users} label="Total Tamu" value={stats.total} testid="stat-total-guests" />
        <StatCard icon={Sparkles} label="Tamu Kembali" value={stats.returning} testid="stat-returning-guests" tone="cyan" />
        <StatCard icon={Crown} label="Tamu Langganan" value={stats.loyal} testid="stat-loyal-guests" tone="terracotta" />
        <StatCard icon={Star} label="Total Revenue" value={formatIDR(stats.totalRevenue)} testid="stat-total-revenue" small />
      </div>

      {/* Search + Country Filter */}
      <div className="bg-white rounded-2xl border border-[#EBE6DE] p-4 shadow-sm flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#586D61]" />
          <Input
            data-testid="input-guest-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Cari nama atau nomor HP…"
            className="pl-9 border-[#EBE6DE]"
          />
        </div>
        {availableCountries.length > 0 && (
          <div className="sm:w-[260px]">
            <select
              data-testid="filter-guest-country"
              value={fCountry}
              onChange={(e) => setFCountry(e.target.value)}
              className="w-full h-10 rounded-md border border-[#EBE6DE] bg-white px-3 text-sm font-medium text-[#172E21] focus:outline-none focus:ring-2 focus:ring-[#D16B4D]"
            >
              <option value="All">🌍 Semua Negara</option>
              {availableCountries.map((c) => (
                <option key={c} value={c}>{flagFor(c)} {c}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Table */}
      {loading ? (
        <div className="bg-white rounded-2xl border border-[#EBE6DE] p-12 flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-[#D16B4D]" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-2xl border border-[#EBE6DE] p-12 text-center">
          <Users className="h-12 w-12 mx-auto text-[#586D61]/40 mb-3" />
          <div className="font-heading text-xl text-[#172E21]">Belum ada data tamu</div>
          <p className="text-sm text-[#586D61] mt-2">Tambahkan booking dulu di menu Bookings.</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-[#EBE6DE] shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <Table data-testid="guests-table">
              <TableHeader>
                <TableRow className="bg-[#FDFBF7]">
                  <TableHead className="font-bold text-[#172E21]">Tamu</TableHead>
                  <TableHead className="font-bold text-[#172E21]">Negara</TableHead>
                  <TableHead className="font-bold text-[#172E21]">Loyalitas</TableHead>
                  <TableHead className="font-bold text-[#172E21] text-right">Stays</TableHead>
                  <TableHead className="font-bold text-[#172E21] text-right">Malam</TableHead>
                  <TableHead className="font-bold text-[#172E21] text-right">Revenue</TableHead>
                  <TableHead className="font-bold text-[#172E21]">Terakhir Menginap</TableHead>
                  <TableHead className="font-bold text-[#172E21] text-right">Aksi</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((g) => (
                  <TableRow
                    key={g.key}
                    data-testid={`guest-row-${g.key}`}
                    className="hover:bg-[#FDFBF7] cursor-pointer"
                    onClick={() => openDetail(g)}
                  >
                    <TableCell>
                      <div className="font-medium text-[#172E21] flex items-center gap-1.5">
                        {g.country && (
                          <span title={g.country} className="text-base leading-none flex-shrink-0">{flagFor(g.country)}</span>
                        )}
                        {g.guest_name || "(tanpa nama)"}
                      </div>
                      <div className="text-xs text-[#586D61] font-mono">
                        {g.phone_number || <span className="italic text-amber-700">Tidak ada HP</span>}
                      </div>
                    </TableCell>
                    <TableCell>
                      {g.country ? (
                        <span data-testid={`guest-country-${g.key}`} className="text-sm text-[#172E21]">{g.country}</span>
                      ) : (
                        <span className="text-xs italic text-[#586D61]/60">—</span>
                      )}
                    </TableCell>
                    <TableCell><TierBadge tier={g.tier} /></TableCell>
                    <TableCell className="text-right font-bold">{g.total_bookings}</TableCell>
                    <TableCell className="text-right">{g.total_nights}</TableCell>
                    <TableCell className="text-right font-bold text-[#D16B4D]">{formatIDR(g.total_revenue)}</TableCell>
                    <TableCell>{formatDate(g.last_check_in)}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        data-testid={`btn-wa-guest-${g.key}`}
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!g.phone_normalized) {
                            toast.error("Tamu ini tidak punya nomor HP");
                            return;
                          }
                          window.open(waUrl(g), "_blank");
                        }}
                        disabled={!g.phone_normalized}
                        className="bg-[#25D366] hover:bg-[#1FA855] text-white rounded-lg h-8"
                      >
                        <MessageCircle className="h-3.5 w-3.5 mr-1" />
                        WA
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {/* Detail Dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-3xl bg-white">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl flex items-center gap-3">
              {selected?.guest_name || "(tanpa nama)"}
              {selected && <TierBadge tier={selected.tier} />}
            </DialogTitle>
            <DialogDescription className="text-[#586D61]">
              {selected?.phone_number ? (
                <span className="flex items-center gap-1.5"><Phone className="h-3.5 w-3.5" />{selected.phone_number}</span>
              ) : "Tidak ada nomor HP"}
            </DialogDescription>
          </DialogHeader>

          {selected && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 my-3">
              <MiniStat label="Total Stays" value={selected.total_bookings} />
              <MiniStat label="Total Malam" value={selected.total_nights} />
              <MiniStat label="Revenue" value={formatIDR(selected.total_revenue)} />
              <MiniStat label="Pertama Menginap" value={formatDate(selected.first_check_in)} />
            </div>
          )}

          <div className="bg-[#FDFBF7] rounded-xl border border-[#EBE6DE] p-3">
            <div className="text-xs font-bold uppercase tracking-wider text-[#586D61] mb-2">Riwayat Booking</div>
            {detailLoading ? (
              <div className="flex justify-center p-6"><Loader2 className="h-5 w-5 animate-spin text-[#D16B4D]" /></div>
            ) : detailBookings.length === 0 ? (
              <div className="text-sm text-[#586D61] text-center py-4">Tidak ada booking</div>
            ) : (
              <div className="space-y-2 max-h-72 overflow-y-auto">
                {detailBookings.map((b) => (
                  <div key={b.id} className="bg-white rounded-lg border border-[#EBE6DE] p-3 flex items-center justify-between gap-3 text-sm">
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-[#172E21]">{b.room_name} · {b.guests} tamu</div>
                      <div className="text-xs text-[#586D61]">
                        {formatDate(b.check_in)} → {formatDate(b.check_out)} ({b.nights} malam) · {b.platform}
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="font-bold text-[#D16B4D]">{formatIDR(b.total_price)}</div>
                      <StatusBadge status={b.status} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex gap-2 pt-2">
            <Button
              data-testid="btn-wa-detail"
              onClick={() => selected && window.open(waUrl(selected), "_blank")}
              disabled={!selected?.phone_normalized}
              className="flex-1 bg-[#25D366] hover:bg-[#1FA855] text-white rounded-xl"
            >
              <MessageCircle className="h-4 w-4 mr-2" />
              Kirim Salam via WhatsApp
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, testid, tone, small }) {
  const toneCls =
    tone === "cyan" ? "bg-cyan-50 text-cyan-700 border-cyan-200" :
    tone === "terracotta" ? "bg-[#D16B4D]/10 text-[#D16B4D] border-[#D16B4D]/20" :
    "bg-white text-[#172E21] border-[#EBE6DE]";
  return (
    <div data-testid={testid} className={`rounded-2xl border p-4 shadow-sm ${toneCls}`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className="h-4 w-4" />
        <div className="text-xs font-bold uppercase tracking-wider opacity-80">{label}</div>
      </div>
      <div className={`font-heading font-semibold ${small ? "text-base" : "text-3xl"}`}>{value}</div>
    </div>
  );
}

function MiniStat({ label, value }) {
  return (
    <div className="bg-white rounded-xl border border-[#EBE6DE] p-3">
      <div className="text-[10px] font-bold uppercase tracking-wider text-[#586D61]">{label}</div>
      <div className="font-heading text-lg font-semibold text-[#172E21] mt-0.5">{value}</div>
    </div>
  );
}
